<template>
    <div class="scan-page">
        <!-- Barcode scanner -->
        <scanner v-if="!currentItem" :sound="false" @barcodeHasBeenScanned="barcodeScanned($event)" ref="scanner"></scanner>



        <!-- Manual entry -->
        <div class="container">

          <div class="row">
                <div class="col-7">
                    <label>Manually enter serial no.</label>
                </div>
                <div class="col-5">
                    <router-link v-if="!currentItem" :to="{ name: 'relocateStock'}" class="float-right back-button text-muted">Relocate Stock <i class="fal fa-arrow-right"></i></router-link>
                    <div v-else  @click="clearPage" class="float-right back-button text-muted" style="cursor: pointer"><i class="fal fa-arrow-left"></i> Reset Lookup</div>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <div class="form-group mb-0">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text"><i class="fa fa-search mb-0"></i></span>
                            </div>
                            <input type="text" inputmode="numeric" pattern="[0-9]*" class="form-control"
                                   placeholder="Enter Serial Number or Location ID" v-on:keyup.enter="handleSearch()" v-model="serialNumber">
                            <span class="input-clear" v-if="serialNumber" @click="clearSerialNumber()"><i class="fas fa-times-circle mb-0"></i></span>
                        </div>
                    </div>
                </div>
                <div class="col-auto pl-0">
                    <button class="btn btn-success btn-block d-md-inline-block py-2" @click="handleSearch()"
                            :disabled="!serialNumber || isLoading">
                        <span class="d-md-none"><i class="fa fa-search mr-0"></i></span>
                        <span class="d-none d-md-inline">Search</span>
                    </button>
                </div>
            </div>

            <!-- Validating overlay -->
            <loading-state
                v-if="isLoading"
                overlay
                title="Looking up Zones"
            />

            <!-- Invalid IMEI -->
            <empty-state
                v-if="serialNumber && errorMessage"
                statusIcon="error"
                :title="`<span class='font-weight-light'>${ errorMessage }</span>`"
            />

            <hr/>

            <div class="row" v-if="currentItem">
                <div class="col-12">
                    <h5 class="text-center">
                        {{ itemName }}
                    </h5>
                </div>
            </div>
          <b-tabs v-if="currentItem">
            <b-tab title="Details">
              <b-card class="p-3 shadow-sm">
                <b-row class="mb-2">
                  <b-col cols="4" class="font-weight-bold text-muted">ID</b-col>
                  <b-col cols="8">{{ currentItem.ssn_id }}</b-col>
                </b-row>

                <b-row class="mb-2">
                  <b-col cols="4" class="font-weight-bold text-muted">Part Number</b-col>
                  <b-col cols="8">{{ currentItem.ssn_part_number}}</b-col>
                </b-row>

                <b-row class="mb-2">
                  <b-col cols="4" class="font-weight-bold text-muted">Description</b-col>
                  <b-col cols="8">{{ currentItem.ssn_description}}</b-col>
                </b-row>

                <b-row class="mb-2">
                  <b-col cols="4" class="font-weight-bold text-muted">Current Location</b-col>
                  <b-col cols="8">{{ currentItem.ssn_currentLocation + (currentItem.ssn_model ? " @ " + currentItem.ssn_model : "") }}</b-col>
                </b-row>

                <b-row>
                  <b-col cols="4" class="font-weight-bold text-muted">Date Last Moved</b-col>
                  <b-col cols="8">{{currentItem.date_moved}}</b-col>
                </b-row>
              </b-card>

            </b-tab>
            <b-tab title="Locations" v-if="currentLocation || pickingLocations.length || holdingLocations.length" active>

              <b-card @click="goToRelocate" class="p-3 shadow-sm" title="Current Location" style="cursor: pointer;" v-if="currentLocation">
                <b-row class="mb-2">
                  <b-col cols="4" class="font-weight-bold text-muted">{{currentLocation?.code ? 'Code' : 'Id'}}</b-col>
                  <b-col cols="8"> {{ currentLocation?.code ? currentLocation.code : currentLocation.id }}</b-col>
                </b-row>

                <b-row class="mb-2">
                  <b-col cols="4" class="font-weight-bold text-muted">Location</b-col>
                  <b-col cols="8">{{ currentLocation?.name ?? "N/A" }}</b-col>
                </b-row>
              </b-card>

              <b-card @click="goToRelocate" class="p-3 shadow-sm" title="Picking Locations" style="cursor: pointer;" v-if="pickingLocations.length">
                <b-row
                    v-for="(location, index) in pickingLocations"
                    :key="location.id"
                    :class="{'mb-3 p-2': true, 'border-bottom': index !== pickingLocations.length - 1}"
                >
                  <b-col cols="6">
                    <strong class="font-weight-bold text-muted">Code:</strong> {{ location.code }}
                  </b-col>
                  <b-col cols="6">
                    <strong class="font-weight-bold text-muted">Location:</strong> {{ location.name }}
                  </b-col>
                </b-row>
              </b-card>

              <b-card @click="goToRelocate" class="p-3 shadow-sm" title="Holding Locations" style="cursor: pointer;" v-if="holdingLocations.length">
                <b-row
                    v-for="(location, index) in holdingLocations"
                    :key="location.id"
                    :class="{'mb-3 p-2': true, 'border-bottom': index !== holdingLocations.length - 1}"
                >
                  <b-col cols="6">
                    <strong class="font-weight-bold text-muted">Code:</strong> {{ location.code }}
                  </b-col>
                  <b-col cols="6">
                    <strong class="font-weight-bold text-muted">Location:</strong> {{ location.name }}
                  </b-col>
                </b-row>
              </b-card>
            </b-tab>
          </b-tabs>

          <b-card title="Location Details" class="shadow-sm" v-if="inventoryLocation">
            <!-- Location ID and Name -->
            <div class="mb-3">
              <b-row class="align-items-center">
                <b-col cols="4" class="font-weight-bold text-muted">Location ID:</b-col>
                <b-col cols="8">
                  <span class="font-weight-bold">{{ inventoryLocation.location.id }}</span>
                </b-col>
              </b-row>
              <b-row class="align-items-center">
                <b-col cols="4" class="font-weight-bold text-muted">Location Name:</b-col>
                <b-col cols="8">
                  <span class="font-weight-bold">{{ inventoryLocation.location.name }}</span>
                </b-col>
              </b-row>
            </div>

            <!-- Search Bar -->
            <div class="form-group mb-3">
              <input
                type="text"
                class="form-control"
                placeholder="Search by Part Number or Description"
                v-model="searchQuery"
              />
            </div>

            <!-- Scrollable List -->
            <div class="item-list" style="max-height: 300px; overflow-y: auto;">
              <div
                v-for="(serial_number, index) in filteredItems"
                :key="serial_number.part_number"
                :class="{'p-3': true, 'border-bottom': index !== filteredItems.length - 1}"
              >
                <b-row>
                  <b-col cols="4" class="font-weight-bold text-muted">Part Number</b-col>
                  <b-col cols="8">{{ serial_number.part_number }}</b-col>
                </b-row>
                <b-row>
                  <b-col cols="4" class="font-weight-bold text-muted">Description</b-col>
                  <b-col cols="8">{{ serial_number.description }}</b-col>
                </b-row>
                <b-row>
                  <b-col cols="4" class="font-weight-bold text-muted">Quantity</b-col>
                  <b-col cols="8">{{ serial_number.quantity }}</b-col>
                </b-row>
              </div>
            </div>
          </b-card>

        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { debounce } from 'lodash';

/**
 * Zone Lookup by part serial number.
 * This component and supporting amends where added as part of WEF-1112
 * @see https://we-fix.atlassian.net/browse/WEF-1112
 */
export default {
    data() {
        return {
            serialNumber: null,
            locationId: null,
            errorMessage: null,
            isLoading: false,
            itemName: null,
            pickingLocations: [],
            holdingLocations: [],
            inventoryLocation: null,
            currentItem: null,
            searchQuery: "",
            debouncedSearchQuery: "",
            currentLocation: {name: null, code: null},
        }
    },

    watch: {
        serialNumber: function () {
            this.errorMessage = null;
        },
        searchQuery: {
            handler: debounce(function (newQuery) {
                this.debouncedSearchQuery = newQuery.toLowerCase();
            }, 400), // Debounce the search query to reduce frequent updates
            immediate: true,
        },
    },

    computed: {
        filteredItems() {
            if (!this.debouncedSearchQuery) {
                return this.inventoryLocation.items;
            }
            const query = this.debouncedSearchQuery;
            return this.inventoryLocation.items.filter(
                (item) =>
                    item.part_number.toLowerCase().includes(query) ||
                    item.description.toLowerCase().includes(query)
            );
        }
    },

    methods: {
        ...mapActions([
            "displayToast"
        ]),

      goToRelocate() {
          this.$router.push({
              name: 'relocateStock',
          });
      },

        /** Barcode scanned */
        barcodeScanned(barcode) {

            if (this.isLoading) return;
            if(barcode.type === "serialNumber") {
              if (barcode.data && barcode.data.id) {
                this.serialNumber = barcode.data.id;
                this.fetchData(barcode.data.type, barcode.data.id);
              } else {
                this.$refs.scanner.showModal("Error with scanning, please try again.");
              }
            } else if( barcode.type === "location") {
                this.loadLocation(barcode.id)
            } else {
                this.$refs.scanner.showModal("This is not a valid QR code");
            }
        },

      clearPage(){
        this.currentItem = null;
        this.inventoryLocation = null;
        this.pickingLocations = [];
        this.holdingLocations = [];
        this.currentLocation = null;
      },

      loadLocation(locationId) {
        this.clearPage();
        this.isLoading = true;
        this.$http.get(`/api/v5/location/location-and-items?location_id=${locationId}`).then((response) => {
          if(response.data.errors){
            this.$refs.scanner.showModal(response.data.errors);
            this.isLoading = false;
            return;
          }
          this.inventoryLocation = response.data;
          this.isLoading = false;
        }).catch((error) => {
          this.isLoading = false;
          let errorMsg;
          if (error.response && error.response.data && error.response.data.errors) {
            errorMsg = error.response.data.errors;
          } else {
            errorMsg = "An unexpected error occurred.";
          }
          this.$refs.scanner.showModal(errorMsg);
        });
      },

        /** Clear the input */
        clearSerialNumber() {
            if (!this.isLoading) {
                this.serialNumber = null;
                this.itemName = null;
            }
        },

        /** Fetch data from API endpoint. **/
        fetchData(barcode_type, barcode_id) {
          this.clearPage();
            this.isLoading = true;
            this.$http.get(`/api/v5/location/locations-for-item?barcode_type=${barcode_type}&barcode_id=${barcode_id}`).then((response) => {
              this.currentItem = response.data;
              this.itemName = response.data.item_name;
              this.processLocations(this.currentItem.item_locations);
                this.isLoading = false;
            }).catch((error) => {
                this.isLoading = false;
                let errorMsg = null;
              if (error.response && error.response.data && error.response.data.errors) {
                errorMsg = error.response.data.errors; // Display the error message
              } else {
                errorMsg = "An unexpected error occurred."; // Fallback message
              }
                // this.displayToast({text: "Error with fetching.", type: 'error'})

                this.$refs.scanner.showModal(errorMsg);
                // this.errorMessage = error;
            });
        },

        processLocations(locations) {
            if(!this.currentItem || !locations) {
                return;
            }
            this.currentLocation = this.currentItem.currentLocation ?? {name: this.currentItem.ssn_model, id: this.currentItem.ssn_currentLocation ?? "NA"};
            this.pickingLocations = [];
            this.holdingLocations = [];
            for (let i = 0; i < locations.length; i++) {
                if (this.currentItem.currentLocation && locations[i].id === this.currentItem.currentLocation.id) {
                    continue;
                }
                if (locations[i].availableToPick) {
                    this.pickingLocations.push(locations[i]);
                } else {
                    this.holdingLocations.push(locations[i]);
                }
            }
        },

        handleSearch() {
          this.fetchData('serialNumber', this.serialNumber); // Default functionality
        }
    }
}
</script>
